/**
 * Basisrekenen (REK) — 17 families, 7 skills
 *
 * Skills: tafels (3), cijferend (4), handig (3), schatten (1),
 *         volgorde (1), context (3), gemengd (2)
 *
 * Created 17 maart 2026 (REK module Fase 0).
 * Based on REK_MODULE_ANALYSE.md + REK_MODULE_KRITISCHE_AUDIT.md.
 *
 * Migrated from GET:
 * - REK-SCHATTEN ← GET-SCHATSTRATEGIE
 * - REK-VOLGORDE ← GET-VOLGORDE-BEWERKING
 * - REK-HANDIG-PLUS-MIN bevat gemigreerde content uit GET-HOOFDREKENEN (Q001/Q002/Q004)
 * - REK-HANDIG-KEER bevat gemigreerde content uit GET-HOOFDREKENEN (Q003/Q005)
 *
 * Audit beslissingen:
 * - GEMENGD-TOETS: tier 12 (E7+), verhoogd van M7(9) — same-tier prereqs opgelost
 * - CONTEXT-MEERSTAPS: Bloom 4 (Analyseren), gecorrigeerd van 5 (Evalueren)
 * - Module naam: "Basisrekenen" (niet "Rekenen" — voorkomt verwarring met GET)
 */

import type { FamilyDefinition } from '../types'

export const REK_FAMILIES: FamilyDefinition[] = [
  // 2.1 Skill: tafels (3 families)
  {
    familyId: 'REK-TAFELS-BASIS',
    module: 'Basisrekenen',
    skill: 'tafels',
    subSkill: 'tafels_recall',
    tier: 1,
    levelCode: 'B6',
    description: 'Tafels 1-10: directe recall',
  },
  {
    familyId: 'REK-TAFELS-TOEPASSEN',
    module: 'Basisrekenen',
    skill: 'tafels',
    subSkill: 'tafels_context',
    tier: 3,
    levelCode: 'M6',
    description: 'Tafels toepassen in context',
  },
  {
    familyId: 'REK-TAFELS-UITBREIDEN',
    module: 'Basisrekenen',
    skill: 'tafels',
    subSkill: 'tafels_uitgebreid',
    tier: 5,
    levelCode: 'E6',
    description: 'Tafels >10, kruistafels, deeltafels',
  },

  // 2.2 Skill: cijferend (4 families)
  {
    familyId: 'REK-CIJF-PLUS',
    module: 'Basisrekenen',
    skill: 'cijferend',
    subSkill: 'cijferend_optellen',
    tier: 1,
    levelCode: 'B6',
    description: 'Cijferend optellen (meercijferig)',
  },
  {
    familyId: 'REK-CIJF-MIN',
    module: 'Basisrekenen',
    skill: 'cijferend',
    subSkill: 'cijferend_aftrekken',
    tier: 2,
    levelCode: 'B6+',
    description: 'Cijferend aftrekken (met lenen)',
  },
  {
    familyId: 'REK-CIJF-KEER',
    module: 'Basisrekenen',
    skill: 'cijferend',
    subSkill: 'cijferend_vermenigvuldigen',
    tier: 3,
    levelCode: 'M6',
    description: 'Cijferend vermenigvuldigen',
  },
  {
    familyId: 'REK-CIJF-DEEL',
    module: 'Basisrekenen',
    skill: 'cijferend',
    subSkill: 'cijferend_delen',
    tier: 7,
    levelCode: 'B7',
    description: 'Cijferend delen (staartdeling)',
  },

  // 2.3 Skill: handig (3 families)
  {
    familyId: 'REK-HANDIG-PLUS-MIN',
    module: 'Basisrekenen',
    skill: 'handig',
    subSkill: 'handig_optellen_aftrekken',
    tier: 3,
    levelCode: 'M6',
    description: 'Handig optellen/aftrekken: splitsen, compenseren, rijgen',
  },
  {
    familyId: 'REK-HANDIG-KEER',
    module: 'Basisrekenen',
    skill: 'handig',
    subSkill: 'handig_vermenigvuldigen',
    tier: 7,
    levelCode: 'B7',
    description: 'Handig vermenigvuldigen: verdubbelen, halveren, factoren',
  },
  {
    familyId: 'REK-HANDIG-DEEL',
    module: 'Basisrekenen',
    skill: 'handig',
    subSkill: 'handig_delen',
    tier: 9,
    levelCode: 'M7',
    description: 'Handig delen: herhaald aftrekken, factoren',
  },

  // 2.4 Skill: schatten (1 family — gemigreerd van GET-SCHATSTRATEGIE)
  {
    familyId: 'REK-SCHATTEN',
    module: 'Basisrekenen',
    skill: 'schatten',
    subSkill: 'schatstrategie',
    tier: 6,
    levelCode: 'E6+',
    description:
      'Rekenuitkomst schatten vóór exact uitrekenen (afronden, referentiegetallen)',
  },

  // 2.5 Skill: volgorde (1 family — gemigreerd van GET-VOLGORDE-BEWERKING)
  {
    familyId: 'REK-VOLGORDE',
    module: 'Basisrekenen',
    skill: 'volgorde',
    subSkill: 'volgorde_bewerkingen',
    tier: 9,
    levelCode: 'M7',
    description: 'Haakjes en volgorde bewerkingen (CITO eindtoets)',
  },

  // 2.6 Skill: context (3 families)
  {
    familyId: 'REK-CONTEXT-PLUS-MIN',
    module: 'Basisrekenen',
    skill: 'context',
    subSkill: 'context_optellen_aftrekken',
    tier: 7,
    levelCode: 'B7',
    description: 'Redactiesommen: optellen & aftrekken',
  },
  {
    familyId: 'REK-CONTEXT-KEER-DEEL',
    module: 'Basisrekenen',
    skill: 'context',
    subSkill: 'context_vermenigvuldigen_delen',
    tier: 9,
    levelCode: 'M7',
    description: 'Redactiesommen: vermenigvuldigen & delen',
  },
  {
    familyId: 'REK-CONTEXT-MEERSTAPS',
    module: 'Basisrekenen',
    skill: 'context',
    subSkill: 'context_meerstaps',
    tier: 11,
    levelCode: 'E7',
    description:
      'Meerstaps-opgaven: combinatie bewerkingen (Bloom 4: Analyseren)',
  },

  // 2.7 Skill: gemengd (2 families)
  {
    familyId: 'REK-GEMENGD-BASIS',
    module: 'Basisrekenen',
    skill: 'gemengd',
    subSkill: 'gemengd_bewerkingen',
    tier: 6,
    levelCode: 'E6+',
    description: 'Mix van bewerkingen, keuze strategie',
  },
  {
    familyId: 'REK-GEMENGD-TOETS',
    module: 'Basisrekenen',
    skill: 'gemengd',
    subSkill: 'gemengd_toets',
    tier: 12,
    levelCode: 'E7+',
    description: 'Toets-achtige mix: alle bewerkingen + context',
  },
]
